/* ============================================================================
 * wishlist-vendor.js — v10.4 (v10.3 + SUBLIMIC integration)
 *  - <td:first-child> を Grid 化して "type" "vendor" "title" "img" "price" の5行に整列
 *  - TYPE: /products/<handle>?section_id=wl-type から取得して .wl-type に表示
 *  - ブランド: /products/<handle>?section_id=wl-brand から取得して .wl-vendor に表示（空なら非表示）
 *  - 価格: 必ず <span class="wl-price"> を生成し .wl-pricebar(grid-area:price) に内包
 *  - レビュー: /products/<handle>?section_id=wl-rating-json を fetch して
 *               .wl-pricebar の直下に星＋「4.6（3件）」を表示
 *  - カート:
 *      * Locksmith で locked=true の商品 → 「商品ページへ」
 *      * 【追加】wl-brand が "SUBLIMIC" の商品 → 「商品ページへ」
 *      * それ以外の商品のみ .wl-add-btn で /cart/add.js に在庫あり最初のvariantをPOST
 *  - ハートUI: 右下セル / オーバーレイ / 任意のハート関連ボタンをすべて非表示
 *  - 行余白: <tr> の padding を 16px 14px 24px 14px に統一
 *  - 後描画: 行やハートが後から挿入されても非表示・整列を維持（MutationObserver）
 *  - 保　険: 2秒で .wl-ready を必ず付与（白画面を防止）
 * ========================================================================== */

(function () {
  'use strict';

  /* ---------- selectors & const ---------- */
  const WRAP_SEL       = '#wl-page .wl-guard, #wl-page .account--favorite-list';
  const TABLE_SEL      = '#favorite-products-table';
  const ROW_SEL        = '#favorite-products-table > tr, #favorite-products-table > tbody > tr';
  const TITLE_WRAP_SEL = '.product-title, .product_title, .product__title, .title';
  const IMG_WRAP_SEL   = '.product-img-container';
  const PRICE_SEL      = '.price, .product-price, .prices, .price-wrapper, .custom-price-area, .wl-price';
  const HEART_SEL      = '.account-favorite-button, .wishlist-button, .favorite-button, [data-wishlist], [data-favorite]';
  const OVERLAY_SEL    = '.sa-008-uw-favorite-app';

  const TR_PADDING     = '16px 14px 24px 14px';  // top right bottom left
  const REVEAL_TIMEOUT = 2000;                   // 2s で強制表示
  const REVEAL_TARGET  = 4;                      // 何枚できたら表示切替

  // SUBLIMIC: wl-brand がこの値のとき「商品ページへ」
  const SUBLIMIC_KEYWORD = 'SUBLIMIC';

  /* ---------- caches/state ---------- */
  const typeCache      = new Map();  // handle -> TYPE text
  const brandCache     = new Map();  // handle -> BRAND text (metafield custom.brand)
  const productCache   = new Map();  // handle -> /products/<handle>.js
  const variantCache   = new Map();  // handle -> {id, available}
  const ratingCache    = new Map();  // handle -> {rating, count}
  const ratingInflight = new Map();  // handle -> Promise<{rating,count}>

  // Locksmith: handle -> locksmith state({locked,...}) or null
  const locksmithCache    = new Map();
  const locksmithInflight = new Map();

  // PATCH: WeakSet を再生成でリセットできるよう let 化
  let processed = new WeakSet();
  const queue   = new Set();

  let rafId = 0, shown = 0, timer = 0, ready = false;

  /* ---------- helpers ---------- */
  function setI(el, prop, val){ if (el) try{ el.style.setProperty(prop, String(val), 'important'); }catch(_){} }
  function qs(root, sel){ return root ? root.querySelector(sel) : null; }
  function qsa(root, sel){ return root ? Array.from(root.querySelectorAll(sel)) : []; }
  function safeHandleFromLink(a){
    try{
      const u = new URL(a.href, location.origin);
      if (u.pathname.includes('products_preview') || u.search.includes('preview_key=')) return '';
      const m = u.pathname.match(/\/products\/([^/]+)/);
      return m ? m[1] : '';
    }catch{ return ''; }
  }
  function revealNow(){
    if (ready) return;
    ready = true;
    const wrap = document.querySelector(WRAP_SEL);
    if (wrap) wrap.classList.add('wl-ready');
    if (timer){ clearTimeout(timer); timer = 0; }
  }
  function maybeReveal(){
    if (ready) return;
    const table = document.querySelector(TABLE_SEL);
    const total = table ? table.querySelectorAll(':scope > tr, :scope > tbody > tr').length : 0;
    if (++shown >= Math.max(1, Math.min(REVEAL_TARGET, total))) revealNow();
  }

  /* ---------- Locksmith storefront helper ---------- */
  function getLocksmithState(handle){
    if (!handle) return Promise.resolve(null);
    if (locksmithCache.has(handle))   return Promise.resolve(locksmithCache.get(handle));
    if (locksmithInflight.has(handle)) return locksmithInflight.get(handle);

    const path = '/products/' + handle;
    const url  = '/apps/locksmith/api/resources?urls[0]=' + encodeURIComponent(path);

    const p = fetch(url, { credentials: 'same-origin' })
      .then(function(r){ return r && r.ok ? r.text() : ''; })
      .then(function(txt){
        let data = null;
        try{
          const json = JSON.parse(txt || '{}');
          data = json[path] || null;
        }catch(_){}
        locksmithCache.set(handle, data);
        locksmithInflight.delete(handle);
        return data;
      })
      .catch(function(){
        locksmithCache.set(handle, null);
        locksmithInflight.delete(handle);
        return null;
      });

    locksmithInflight.set(handle, p);
    return p;
  }

  /* ---------- grid scaffolding ---------- */
  function ensureGrid(td){
    setI(td, 'display', 'grid');
    setI(td, 'grid-template-areas', '"type" "vendor" "title" "img" "price"');
    setI(td, 'grid-auto-rows', 'auto');
    setI(td, 'align-items', 'start');
    setI(td, 'justify-items', 'start'); // TYPEピルが横いっぱいに伸びないように
    setI(td, 'row-gap', '6px');
    setI(td, 'text-align', 'left');
  }

  /* ---------- TYPE / BRAND ---------- */
  function fetchType(handle){
    if (typeCache.has(handle)) return Promise.resolve(typeCache.get(handle));
    return fetch('/products/' + handle + '?section_id=wl-type', { credentials:'same-origin' })
      .then(r => r.ok ? r.text() : '')
      .then(html => {
        let val = '';
        try{
          const isDoc = /<!doctype|<html/i.test(html||'');
          const doc = new DOMParser().parseFromString(html||'', 'text/html');
          const el  = doc.querySelector('.wl-type-text') || (!isDoc ? doc.body : (doc.body && doc.body.firstElementChild));
          val = el ? (el.textContent||'').trim() : '';
        }catch{}
        typeCache.set(handle, val);
        return val;
      })
      .catch(()=>{ typeCache.set(handle,''); return ''; });
  }
  function fetchBrand(handle){
    if (brandCache.has(handle)) return Promise.resolve(brandCache.get(handle));
    return fetch('/products/' + handle + '?section_id=wl-brand', { credentials:'same-origin' })
      .then(r => r.ok ? r.text() : '')
      .then(html => {
        let val = '';
        try{
          const doc = new DOMParser().parseFromString(html||'', 'text/html');
          const el  = doc.querySelector('.wl-brand-text') || (doc.body && (doc.body.firstElementChild || doc.body));
          val = el ? (el.textContent||'').trim() : '';
        }catch{}
        brandCache.set(handle, val);
        return val;
      })
      .catch(()=>{ brandCache.set(handle,''); return ''; });
  }

  function ensureTypeAsync(td, titleLink){
    return new Promise(resolve=>{
      try{
        let el = qs(td,'.wl-type');
        if (!el){
          el = document.createElement('span');
          el.className = 'wl-type';
          setI(el,'grid-area','type');
          td.insertBefore(el, td.firstElementChild || null);
        }
        const handle = titleLink ? (safeHandleFromLink(titleLink)||'') : '';
        if (!handle){ el.remove(); resolve(); return; }
        fetchType(handle).then(val=>{
          if (val){ el.textContent = val; }
          else    { el.remove(); }
          resolve();
        }).catch(()=>{ el.remove(); resolve(); });
      }catch{ resolve(); }
    });
  }

  // ブランドを .wl-vendor に入れる（空なら要素ごと非表示）
  function ensureVendorAsync(td, titleLink){
    return new Promise(resolve=>{
      try{
        let el = qs(td,'.wl-vendor');
        if (!el){
          el = document.createElement('span');
          el.className = 'wl-vendor';
          setI(el,'grid-area','vendor');
          td.insertBefore(el, td.firstElementChild || null);
        }
        const handle = titleLink ? (safeHandleFromLink(titleLink)||'') : '';
        if (!handle){ el.remove(); resolve(); return; }
        fetchBrand(handle).then(val=>{
          if (val){ el.textContent = val; }
          else    { el.remove(); }
          resolve();
        }).catch(()=>{ el.remove(); resolve(); });
      }catch{ resolve(); }
    });
  }

  /* ---------- PRICE & BAR ---------- */
  const YEN_RX = /^\s*[¥￥]?\s*\d[\d,]*\s*(円)?\s*$/;

  function findPriceElBySelectors(td){ const c = qsa(td, PRICE_SEL); return c[0] || null; }
  function createPriceFromTextNode(n){
    if(!n || !n.parentNode) return null;
    const s=(n.textContent||'').trim(); if(!YEN_RX.test(s)) return null;
    const sp=document.createElement('span'); sp.className='wl-price'; sp.textContent=s;
    n.parentNode.replaceChild(sp,n); return sp;
  }
  function findPriceTextNode(td){
    const w=document.createTreeWalker(td, NodeFilter.SHOW_TEXT, null); let k;
    while((k=w.nextNode())){
      const s=(k.textContent||'').trim(); if(!s || !YEN_RX.test(s)) continue;
      const pe=k.parentElement; if(!pe) continue;
      if(pe.closest('.account-favorite-button,.product-img-container,.wl-type,.wl-vendor,a[href*="/products/"]')) continue;
      return k;
    } return null;
  }
  function ensurePriceEl(td, titleLink){
    let el = findPriceElBySelectors(td); if (el) return el;
    if (titleLink){
      const br=(titleLink.nextSibling && titleLink.nextSibling.nodeName==='BR')?titleLink.nextSibling:null;
      if (br && br.nextSibling && br.nextSibling.nodeType===Node.TEXT_NODE){ el=createPriceFromTextNode(br.nextSibling); if (el) return el; }
      if (titleLink.nextSibling && titleLink.nextSibling.nodeType===Node.TEXT_NODE){ el=createPriceFromTextNode(titleLink.nextSibling); if (el) return el; }
    }
    const t=findPriceTextNode(td); if (t) el=createPriceFromTextNode(t);
    return el || null;
  }
  function ensurePriceBar(td, priceEl){
    if (!priceEl) return null;
    let bar = priceEl.parentElement && priceEl.parentElement.classList.contains('wl-pricebar')
            ? priceEl.parentElement : null;
    if (!bar){
      bar = document.createElement('div');
      bar.className = 'wl-pricebar';
      setI(bar,'grid-area','price');
      setI(bar,'display','block');
      priceEl.parentNode.insertBefore(bar, priceEl);
      bar.appendChild(priceEl);
    }else{
      setI(bar,'grid-area','price');
      setI(bar,'display','block');
    }
    setI(priceEl,'margin','0');
    return bar;
  }

  /* ---------- Product / Variant helpers ---------- */
  function getProduct(handle){
    if (productCache.has(handle)) return productCache.get(handle);
    const p = fetch('/products/' + handle + '.js', {credentials:'same-origin'})
      .then(r=> r.ok ? r.json() : null)
      .then(p=>{ productCache.set(handle,p); return p; })
      .catch(()=>{ productCache.set(handle,null); return null; });
    productCache.set(handle, p);
    return p;
  }
  async function getVariantInfo(handle){
    if (variantCache.has(handle)) return variantCache.get(handle);
    const p = await getProduct(handle);
    let id=null, ok=false;
    if (p && Array.isArray(p.variants) && p.variants.length){
      const v = p.variants.find(x=>x && x.available) || p.variants[0];
      if (v){ id=v.id; ok=!!v.available; }
    }
    const info={ id, available: ok }; variantCache.set(handle, info); return info;
  }

  /* ---------- Add to cart / Product page button (Locksmith + SUBLIMIC連携) ---------- */
  function ensureAddToCart(td, titleLink){
    return new Promise(resolve=>{
      try{
        let btn = qs(td, '.wl-add-btn');
        if (!btn){
          btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'wl-add-btn';
          btn.textContent = 'カートに入れる';
          btn.disabled = true;
          td.appendChild(btn);
        }
                // ✅ 通常の商品カードのボタンと同じ見た目に揃える（重要）
setI(btn, 'font-size', '12.5px');
setI(btn, 'font-weight', '500');
setI(btn, 'line-height', '1');

        const handle = titleLink ? (safeHandleFromLink(titleLink) || '') : '';
        if (!handle){
          btn.disabled = true;
          btn.textContent = '商品情報取得不可';
          resolve();
          return;
        }

        // 従来通り「カートに入れる」ボタンとしてセット
        function setupCartButton(){
          return getVariantInfo(handle).then(info=>{
            if (info && info.id){
              btn.dataset.variantId = String(info.id);
              if (info.available){
                btn.disabled = false;
                btn.textContent = 'カートに入れる';
                btn.classList.remove('is-oos');
              }else{
                btn.disabled = true;
                btn.textContent = '在庫なし';
                btn.classList.add('is-oos');
              }
            }else{
              btn.disabled = true;
              btn.textContent = '在庫なし';
              btn.classList.add('is-oos');
            }

            if (!btn.dataset.bound){
              btn.addEventListener('click', async ()=>{
                if (btn.disabled) return;
                const id = btn.dataset.variantId;
                if (!id) return; // ← 商品ページモード時に何もしない保険
                const prev = btn.textContent;
                btn.disabled = true;
                btn.textContent = '追加中…';
                try{
                  const r = await fetch('/cart/add.js', {
                    method:'POST',
                    headers:{
                      'Content-Type':'application/json',
                      'Accept':'application/json'
                    },
                    body: JSON.stringify({ id: Number(id), quantity: 1 })
                  });
                  if (!r || !r.ok) throw 0;
                  btn.textContent = '追加しました';
                  btn.classList.add('is-added');
                  window.dispatchEvent(new CustomEvent('cart:refresh', {detail:{source:'wishlist'}}));
                }catch(_){
                  btn.textContent = '失敗しました';
                  setTimeout(()=>{ btn.textContent = prev; btn.disabled = false; }, 1200);
                }
              });
              btn.dataset.bound = '1';
            }
          }).catch(()=>{
            btn.disabled = true;
            btn.textContent = '在庫なし';
            btn.classList.add('is-oos');
          });
        }

        // ✅ Locksmith + SUBLIMIC 判定をまとめて実行
        Promise.all([
          getLocksmithState(handle).catch(()=>null),
          fetchBrand(handle).catch(()=> '')
        ]).then(([lsState, brandVal])=>{
          const brandNorm = String(brandVal || '').trim().toUpperCase();
          const isSublimic = brandNorm === SUBLIMIC_KEYWORD;

          if ((lsState && lsState.locked) || isSublimic){
            // ロック or SUBLIMIC：商品ページへリンクするボタンにする
            btn.disabled = false;
            btn.textContent = '商品ページへ';
            btn.classList.remove('is-oos');
            btn.classList.remove('is-added');

            // cart用のvariant-idを消して、誤追加を防ぐ
            btn.removeAttribute('data-variant-id');

            if (!btn.dataset.lsToProductBound){
              btn.addEventListener('click', ()=>{
                const href = (titleLink && titleLink.href) ? titleLink.href : ('/products/' + handle);
                window.location.href = href;
              });
              btn.dataset.lsToProductBound = '1';
            }
            resolve();
          }else{
            // 非ロック＆非SUBLIMIC：従来通りカート追加ボタン
            setupCartButton().finally(resolve);
          }
        }).catch(()=>{
          // どちらか取得失敗 → 既存通りカート追加に倒す
          setupCartButton().finally(resolve);
        });

      }catch(_){
        resolve();
      }
    });
  }

  /* ---------- HEARTLESS: hide everything related ---------- */
  function hideHearts(tr){
    try{
      const tdRight = qs(tr, 'td.text-right');
      if (tdRight){
        setI(tdRight, 'display','none');
        setI(tdRight,'width','0');
        setI(tdRight,'padding','0');
      }
      qsa(tr, HEART_SEL).forEach(btn => {
        setI(btn,'display','none');
        setI(btn,'opacity','0');
        setI(btn,'pointer-events','none');
      });
      const ov = document.querySelector(OVERLAY_SEL);
      if (ov){
        setI(ov,'display','none');
        setI(ov,'opacity','0');
        setI(ov,'pointer-events','none');
      }
    }catch(_){}
  }

  /* ---------- RATING (wl-rating-json) ---------- */
  function parseRatingText(txt){
    let rating = 0, count = 0;
    if (!txt) return {rating, count};
    try{
      const j = JSON.parse(txt);
      const r = (typeof j.rating === 'number') ? j.rating : parseFloat(j.rating);
      const cRaw = j.count != null ? j.count : (j.total_reviews != null ? j.total_reviews : 0);
      const c = (typeof cRaw === 'number') ? cRaw : parseInt(String(cRaw),10);
      if (isFinite(r)) rating = r;
      if (isFinite(c)) count  = c;
    }catch(_){
      const m1 = txt.match(/"rating"\s*:\s*([0-9]+(?:\.[0-9]+)?)/);
      const m2 = txt.match(/"(?:count|total_reviews)"\s*:\s*([0-9]+)/);
      if (m1) rating = parseFloat(m1[1]) || 0;
      if (m2) count  = parseInt(m2[1],10) || 0;
    }
    return {rating, count};
  }

  function fetchRating(handle){
    if (!handle) return Promise.resolve({rating:0, count:0});
    if (ratingCache.has(handle))   return Promise.resolve(ratingCache.get(handle));
    if (ratingInflight.has(handle)) return ratingInflight.get(handle);

    const p = fetch('/products/' + handle + '?section_id=wl-rating-json', {credentials:'same-origin'})
      .then(r => r && r.ok ? r.text() : '')
      .then(txt => {
        const data = parseRatingText(txt);
        ratingCache.set(handle, data);
        ratingInflight.delete(handle);
        return data;
      })
      .catch(()=> {
        const data = {rating:0, count:0};
        ratingCache.set(handle, data);
        ratingInflight.delete(handle);
        return data;
      });

    ratingInflight.set(handle, p);
    return p;
  }

  function ensureReviewAsync(td, titleLink){
    return new Promise(resolve=>{
      try{
        const priceEl = ensurePriceEl(td, titleLink);
        const bar     = ensurePriceBar(td, priceEl);
        if (!bar){ resolve(); return; }

        let slot = qs(bar, '.wl-review');
        if (!slot){
          slot = document.createElement('div');
          slot.className = 'wl-review';
          slot.textContent = '0.0（0件）';
          bar.appendChild(slot);
        }

        const handle = titleLink ? (safeHandleFromLink(titleLink)||'') : '';
        if (!handle){ slot.textContent = '0.0（0件）'; resolve(); return; }

        fetchRating(handle).then(({rating, count})=>{
          const score = Math.max(0, Math.min(5, Number(rating)||0));
          const cnt   = Number.isFinite(count) ? count : 0;

          const percent = (score / 5 * 100).toFixed(1);
          const display = score.toFixed(1);

          slot.innerHTML =
            '<span class="wl-stars" role="img" aria-label="' + display + ' / 5">' +
              '<span class="wl-stars__bg">★★★★★</span>' +
              '<span class="wl-stars__fg" style="width:' + percent + '%;">★★★★★</span>' +
            '</span>' +
            '<span class="wl-score">' + display + '</span>' +
            '<span class="wl-count">（' + cnt + '件）</span>';
          resolve();
        }).catch(()=>{ slot.textContent = '0.0（0件）'; resolve(); });
      }catch(_){ resolve(); }
    });
  }

  /* ---------- per-row assembly ---------- */
  function arrange(tr){
    return new Promise(resolve=>{
      try{
        const td = qs(tr,'td:first-child');
        if (!td){ resolve(); return; }

        setI(tr, 'padding', TR_PADDING);

        ensureGrid(td);

        let titleLink=null, maxLen=-1;
        qsa(td,'a[href*="/products/"]').forEach(a=>{
          const h = safeHandleFromLink(a);
          const L = (a.textContent||'').trim().length;
          if (h && L>maxLen){ maxLen=L; titleLink=a; }
        });

        const titleBlock = titleLink ? (titleLink.closest(TITLE_WRAP_SEL) || titleLink) : null;
        const imgWrap    = qs(td, IMG_WRAP_SEL);
        if (titleBlock){ setI(titleBlock,'grid-area','title'); }
        if (imgWrap){
          setI(imgWrap,'grid-area','img');
          setI(imgWrap,'float','none');
          setI(imgWrap,'text-align','center');
        }

        const priceEl = ensurePriceEl(td, titleLink);
        const bar     = ensurePriceBar(td, priceEl);

        const pType   = ensureTypeAsync(td, titleLink);
        const pBrand  = ensureVendorAsync(td, titleLink);
        const pReview = ensureReviewAsync(td, titleLink);
        const pAdd    = ensureAddToCart(td, titleLink);

        hideHearts(tr);

        Promise.allSettled([pType, pBrand, pReview, pAdd]).then(()=>{
          if (bar && bar!==td.lastElementChild) td.appendChild(bar);
          tr.classList.add('wl-card-ready');
          maybeReveal();
          resolve();
        });
      }catch(_){
        tr.classList.add('wl-card-ready');
        maybeReveal();
        resolve();
      }
    });
  }

  /* ---------- queue & observers ---------- */
  function queueRow(tr){
    if (processed.has(tr)) return;
    processed.add(tr);
    queue.add(tr);
    if (rafId) return;
    rafId = requestAnimationFrame(()=>{
      rafId=0;
      const jobs=[]; queue.forEach(r=>jobs.push(arrange(r)));
      queue.clear();
      Promise.allSettled(jobs).then(()=> setTimeout(()=> { processed = new WeakSet(); }, 0));
    });
  }

  function boot(table){
    if (!table) return;

    qsa(table, ':scope > tr, :scope > tbody > tr').forEach(queueRow);

    new MutationObserver(ms=>{
      ms.forEach(m=>{
        (m.addedNodes||[]).forEach(n=>{
          if (n.nodeType!==1) return;
          if (n.matches && n.matches(ROW_SEL)) queueRow(n);
          else qsa(n, ROW_SEL).forEach(queueRow);

          if (n.matches && n.matches(HEART_SEL)) setI(n,'display','none');
          qsa(n, HEART_SEL).forEach(btn=> setI(btn,'display','none'));
        });
      });
    }).observe(table, {childList:true, subtree:true});

    new MutationObserver(ms=>{
      ms.forEach(m=>{
        (m.addedNodes||[]).forEach(n=>{
          if (n.nodeType!==1) return;
          if (n.matches && n.matches(OVERLAY_SEL)){
            setI(n,'display','none');
            setI(n,'opacity','0');
            setI(n,'pointer-events','none');
          }
          const inner = qs(n, OVERLAY_SEL);
          if (inner){
            setI(inner,'display','none');
          }
        });
      });
    }).observe(document.body, {childList:true, subtree:true});

    if (!timer) timer = setTimeout(revealNow, REVEAL_TIMEOUT);
  }

  function init(){
    const table = document.querySelector(TABLE_SEL);
    if (table){ boot(table); return; }

    const mo = new MutationObserver(ms=>{
      for (const m of ms){
        for (const n of m.addedNodes||[]){
          if (n.nodeType!==1) continue;
          if (n.matches && n.matches(TABLE_SEL)){ mo.disconnect(); boot(n); return; }
          const t = qs(n, TABLE_SEL);
          if (t){ mo.disconnect(); boot(t); return; }
        }
      }
    });
    mo.observe(document.body, {childList:true, subtree:true});

    if (!timer) timer = setTimeout(revealNow, REVEAL_TIMEOUT);
  }

  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded', init, {once:true});
  else init();

})();
